import { useMemo, useState, useEffect, useCallback, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Box, Button, Card, CardContent, Chip, Divider, Grid, IconButton,
  Menu, MenuItem, ListItemIcon, ListItemText, Stack, Switch, Tooltip, Typography,
  useMediaQuery, useTheme, alpha, LinearProgress,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  Add, Edit, Delete, MoreVert, People,
  CardMembership,
} from '@mui/icons-material';
import ConfirmDialog from '../ui/ConfirmDialog';
import { withSerialNo } from '../ui/gridColumns';
import PartnerSubscriptionPlanFormDrawer from './PartnerSubscriptionPlanFormDrawer';
import PartnerPlanSubscribersDrawer from './PartnerPlanSubscribersDrawer';
import {
  PARTNER_PLAN_BILLING_INTERVALS,
  PARTNER_PLAN_ALERT_SX,
} from '../../constants/partnerSubscriptionPlanConfig';
import {
  listPartnerSubscriptionPlans,
  createPartnerSubscriptionPlan,
  updatePartnerSubscriptionPlan,
  setPartnerSubscriptionPlanStatus,
  deletePartnerSubscriptionPlan,
  getPartnerPlanUsage,
  extractApiError,
  getPartnerPlanPriceDisplay,
  formatPartnerPlanTypesLabel,
  partnerPlanBillingLabel,
  zoneLabels,
  type PartnerSubscriptionPlanPayload,
  type PartnerSubscriptionPlanRow,
  type PartnerPlanUsage,
} from '../../services/partnerSubscriptionPlanService';

interface Props {
  zoneId?: string;
  zoneName?: string;
  zoneCode?: string;
  compact?: boolean;
  showHeader?: boolean;
  onPlansChange?: () => void;
  /** Increment to open the add-plan drawer from a parent (e.g. Master Data header). */
  addPlanRequestId?: number;
}

const ZONE_CHIP_SX = {
  height: 24,
  maxWidth: '100%',
  '& .MuiChip-label': { px: 0.75, lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis' },
};

const PARTNER_PLAN_GRID_SX = {
  border: 0,
  '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 },
  '& .MuiDataGrid-columnHeader': { display: 'flex', alignItems: 'center' },
  '& .MuiDataGrid-cell': {
    display: 'flex',
    alignItems: 'center',
    py: 0,
  },
  '& .MuiDataGrid-cellContent': {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    minHeight: '100%',
    overflow: 'hidden',
  },
};

function GridCell({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        minHeight: '100%',
        overflow: 'hidden',
      }}
    >
      {children}
    </Box>
  );
}

function ZoneChips({ labels }: { labels: string[] }) {
  if (!labels.length) {
    return (
      <Typography variant="body2" color="text.disabled" sx={{ lineHeight: 1.4 }}>
        —
      </Typography>
    );
  }
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center" sx={{ width: '100%' }}>
      {labels.slice(0, 3).map((label) => (
        <Chip key={label} size="small" variant="outlined" label={label} sx={ZONE_CHIP_SX} />
      ))}
      {labels.length > 3 && (
        <Chip size="small" label={`+${labels.length - 3}`} variant="outlined" sx={ZONE_CHIP_SX} />
      )}
    </Stack>
  );
}

function PlanPriceCell({ plan }: { plan: PartnerSubscriptionPlanRow }) {
  const price = getPartnerPlanPriceDisplay(plan);

  return (
    <Tooltip title={price.summary} placement="top-start">
      <Box sx={{ py: 0.25, minWidth: 0, lineHeight: 1.35, overflow: 'visible' }}>
        <Typography variant="body2" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
          {price.amount}
        </Typography>
        {(price.cadence || price.validity) && (
          <Typography
            variant="caption"
            color="text.secondary"
            component="span"
            sx={{ display: 'block', whiteSpace: 'normal', wordBreak: 'break-word' }}
          >
            {[price.cadence, price.validity].filter(Boolean).join(' · ')}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}

function PlanStatusToggle({
  plan,
  disabled,
  onToggle,
}: {
  plan: PartnerSubscriptionPlanRow;
  disabled?: boolean;
  onToggle: (plan: PartnerSubscriptionPlanRow, isActive: boolean) => void;
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.5}
      onClick={(e) => e.stopPropagation()}
    >
      <Switch
        size="small"
        checked={!!plan.isActive}
        disabled={disabled}
        inputProps={{ 'aria-label': `${plan.isActive ? 'Deactivate' : 'Activate'} ${plan.name}` }}
        onChange={(e) => onToggle(plan, e.target.checked)}
      />
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 46, lineHeight: 1.2 }}>
        {plan.isActive ? 'Active' : 'Inactive'}
      </Typography>
    </Stack>
  );
}

function PlanCard({
  plan,
  onEdit,
  onMenu,
  onViewPartners,
  onToggleStatus,
  statusToggleDisabled,
}: {
  plan: PartnerSubscriptionPlanRow;
  onEdit: () => void;
  onMenu: (el: HTMLElement) => void;
  onViewPartners: () => void;
  onToggleStatus: (plan: PartnerSubscriptionPlanRow, isActive: boolean) => void;
  statusToggleDisabled?: boolean;
}) {
  const theme = useTheme();
  const subs = plan.activeSubscriberCount ?? 0;

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        borderRadius: 2.5,
        borderColor: plan.isActive ? alpha(theme.palette.success.main, 0.35) : 'divider',
        bgcolor: plan.isActive ? alpha(theme.palette.success.main, 0.03) : 'background.paper',
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Box minWidth={0} flex={1}>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center" mb={0.5}>
              {plan.badgeLabel && <Chip size="small" color="primary" label={plan.badgeLabel} />}
              {plan.isFeatured && <Chip size="small" variant="outlined" label="Featured" />}
              <PlanStatusToggle
                plan={plan}
                disabled={statusToggleDisabled}
                onToggle={onToggleStatus}
              />
            </Stack>
            <Typography variant="subtitle1" fontWeight={800} noWrap>{plan.name}</Typography>
            <Typography variant="caption" color="text.secondary" fontFamily="monospace">{plan.code}</Typography>
          </Box>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onMenu(e.currentTarget); }}>
            <MoreVert fontSize="small" />
          </IconButton>
        </Stack>

        <Typography variant="h6" fontWeight={800} sx={{ mt: 1.5 }}>
          {getPartnerPlanPriceDisplay(plan).amount}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {getPartnerPlanPriceDisplay(plan).summary}
          {' · '}
          {formatPartnerPlanTypesLabel(plan)}
        </Typography>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.75, alignItems: 'center' }}>
          {zoneLabels(plan).map((label) => (
            <Chip key={label} size="small" variant="outlined" label={label} sx={ZONE_CHIP_SX} />
          ))}
        </Stack>

        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
          {(plan.features || []).slice(0, 3).map((f) => (
            <Chip key={f} size="small" variant="outlined" label={f} sx={{ maxWidth: '100%' }} />
          ))}
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
          <Chip
            size="small"
            icon={<People sx={{ fontSize: 14 }} />}
            label={`${subs} partner${subs === 1 ? '' : 's'}`}
            color={subs > 0 ? 'warning' : 'default'}
            variant={subs > 0 ? 'filled' : 'outlined'}
            clickable
            onClick={onViewPartners}
          />
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="View subscribed partners">
              <IconButton size="small" color="primary" onClick={onViewPartners}>
                <People fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button size="small" startIcon={<Edit />} onClick={onEdit}>Edit</Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function PartnerSubscriptionPlanManager({
  zoneId,
  zoneName,
  zoneCode,
  compact = false,
  showHeader = true,
  onPlansChange,
  addPlanRequestId = 0,
}: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const queryClient = useQueryClient();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mode, setMode] = useState<'create' | 'edit'>('create');
  const [selected, setSelected] = useState<PartnerSubscriptionPlanRow | null>(null);
  const [formError, setFormError] = useState('');
  const [mutationError, setMutationError] = useState('');
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [headerMenuAnchor, setHeaderMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuPlan, setMenuPlan] = useState<PartnerSubscriptionPlanRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PartnerSubscriptionPlanRow | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<PartnerSubscriptionPlanRow | null>(null);
  const [usagePreview, setUsagePreview] = useState<PartnerPlanUsage | null>(null);
  const [subscribersPlan, setSubscribersPlan] = useState<PartnerSubscriptionPlanRow | null>(null);
  const [statusToggleId, setStatusToggleId] = useState<string | null>(null);

  const queryKey = ['partner-subscription-plans', zoneId || 'all'];

  const { data: plans = [], isLoading } = useQuery({
    queryKey,
    queryFn: () => listPartnerSubscriptionPlans(zoneId ? { zoneId } : undefined),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['partner-subscription-plans'] });
    onPlansChange?.();
  };

  const createMut = useMutation({
    mutationFn: createPartnerSubscriptionPlan,
    onSuccess: () => { invalidate(); setDrawerOpen(false); setFormError(''); },
    onError: (err) => setFormError(extractApiError(err)),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<PartnerSubscriptionPlanPayload> }) =>
      updatePartnerSubscriptionPlan(id, payload),
    onSuccess: () => { invalidate(); setDrawerOpen(false); setFormError(''); },
    onError: (err) => setFormError(extractApiError(err)),
  });

  const statusMut = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      setPartnerSubscriptionPlanStatus(id, isActive),
    onSuccess: () => { invalidate(); setDeactivateTarget(null); setUsagePreview(null); },
    onError: (err) => setMutationError(extractApiError(err)),
  });

  const deleteMut = useMutation({
    mutationFn: deletePartnerSubscriptionPlan,
    onSuccess: () => { invalidate(); setDeleteTarget(null); setUsagePreview(null); },
    onError: (err) => setMutationError(extractApiError(err)),
  });

  const stats = useMemo(() => ({
    total: plans.length,
    active: plans.filter((p) => p.isActive).length,
    subscribers: plans.reduce((s, p) => s + (p.activeSubscriberCount ?? 0), 0),
  }), [plans]);

  const openCreate = () => {
    setMode('create');
    setSelected(null);
    setFormError('');
    setDrawerOpen(true);
  };

  useEffect(() => {
    if (addPlanRequestId > 0) openCreate();
  }, [addPlanRequestId]);

  const openEdit = (plan: PartnerSubscriptionPlanRow) => {
    setMode('edit');
    setSelected(plan);
    setFormError('');
    setDrawerOpen(true);
    setMenuAnchor(null);
  };

  const openMenu = (el: HTMLElement, plan: PartnerSubscriptionPlanRow) => {
    setMenuAnchor(el);
    setMenuPlan(plan);
  };

  const openSubscribers = (plan: PartnerSubscriptionPlanRow) => {
    setMenuAnchor(null);
    setSubscribersPlan(plan);
  };

  const prepareAction = async (plan: PartnerSubscriptionPlanRow, action: 'delete' | 'deactivate') => {
    setMenuAnchor(null);
    try {
      const usage = await getPartnerPlanUsage(plan._id);
      setUsagePreview(usage);
      if (action === 'delete') setDeleteTarget(plan);
      else setDeactivateTarget(plan);
    } catch (e) {
      setMutationError(extractApiError(e));
    }
  };

  const handleStatusToggle = useCallback((plan: PartnerSubscriptionPlanRow, nextActive: boolean) => {
    if (statusMut.isPending) return;
    if (nextActive) {
      setStatusToggleId(plan._id);
      statusMut.mutate(
        { id: plan._id, isActive: true },
        { onSettled: () => setStatusToggleId(null) },
      );
      return;
    }
    void prepareAction(plan, 'deactivate');
  }, [statusMut]);

  const billingLabel = (value: string) =>
    PARTNER_PLAN_BILLING_INTERVALS.find((o) => o.value === value)?.label || value;

  const columns: GridColDef[] = useMemo(() => withSerialNo([
    { field: 'name', headerName: 'Plan', flex: 1, minWidth: 160 },
    { field: 'code', headerName: 'Code', width: 120 },
    {
      field: 'partnerType',
      headerName: 'Partner types',
      width: 200,
      minWidth: 160,
      valueGetter: (_: unknown, row: PartnerSubscriptionPlanRow) => formatPartnerPlanTypesLabel(row),
    },
    ...(zoneId ? [] : [{
      field: 'zones',
      headerName: 'Zones',
      flex: 1,
      minWidth: 180,
      sortable: false,
      align: 'left',
      headerAlign: 'left',
      renderCell: ({ row }: { row: PartnerSubscriptionPlanRow }) => (
        <GridCell>
          <ZoneChips labels={zoneLabels(row as PartnerSubscriptionPlanRow)} />
        </GridCell>
      ),
    } as GridColDef]),
    {
      field: 'price',
      headerName: 'Price',
      width: 168,
      minWidth: 168,
      sortable: true,
      valueGetter: (_: unknown, row: PartnerSubscriptionPlanRow) => row.price,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minHeight: '100%', overflow: 'visible', py: 0.5 }}>
          <PlanPriceCell plan={row as PartnerSubscriptionPlanRow} />
        </Box>
      ),
    },
    {
      field: 'billingInterval',
      headerName: 'Billing',
      width: 110,
      valueGetter: (_: unknown, row: PartnerSubscriptionPlanRow) => billingLabel(String(row.billingInterval)),
    },
    {
      field: 'validityDays',
      headerName: 'Validity',
      width: 95,
      valueGetter: (_: unknown, row: PartnerSubscriptionPlanRow) => `${row.validityDays} days`,
    },
    {
      field: 'activeSubscriberCount',
      headerName: 'Partners',
      width: 110,
      renderCell: ({ row }) => {
        const plan = row as PartnerSubscriptionPlanRow;
        const count = plan.activeSubscriberCount ?? 0;
        return (
          <GridCell>
            <Tooltip title="View subscribed partners">
              <Chip
                size="small"
                icon={<People sx={{ fontSize: 14 }} />}
                label={count}
                color={count > 0 ? 'warning' : 'default'}
                variant={count > 0 ? 'filled' : 'outlined'}
                clickable
                sx={ZONE_CHIP_SX}
                onClick={(e) => {
                  e.stopPropagation();
                  openSubscribers(plan);
                }}
              />
            </Tooltip>
          </GridCell>
        );
      },
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 130,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: ({ row }) => {
        const plan = row as PartnerSubscriptionPlanRow;
        return (
          <GridCell>
            <Tooltip title={plan.isActive ? 'Toggle off to deactivate plan' : 'Toggle on to activate plan'}>
              <Box>
                <PlanStatusToggle
                  plan={plan}
                  disabled={statusMut.isPending && statusToggleId === plan._id}
                  onToggle={handleStatusToggle}
                />
              </Box>
            </Tooltip>
          </GridCell>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 88,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      headerAlign: 'right',
      align: 'center',
      renderCell: ({ row }) => (
        <GridCell>
          <Tooltip title="Plan actions">
            <IconButton
              size="small"
              aria-label="Plan actions"
              onClick={(e) => {
                e.stopPropagation();
                openMenu(e.currentTarget, row as PartnerSubscriptionPlanRow);
              }}
            >
              <MoreVert fontSize="small" />
            </IconButton>
          </Tooltip>
        </GridCell>
      ),
    },
  ]), [zoneId, handleStatusToggle, statusMut.isPending, statusToggleId]);

  const defaultZoneId = zoneId || '';

  return (
    <Box>
      {showHeader && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={1.5}
          sx={{ mb: 2 }}
        >
          <Box>
            {!compact && (
              <Typography variant="subtitle1" fontWeight={800}>
                {zoneName ? `Plans for ${zoneName}` : 'Partner subscription plans'}
              </Typography>
            )}
            {zoneCode && (
              <Typography variant="caption" color="text.secondary">Zone code: {zoneCode}</Typography>
            )}
          </Box>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title="More actions">
              <IconButton
                size="small"
                aria-label="Subscription plan menu"
                onClick={(e) => setHeaderMenuAnchor(e.currentTarget)}
              >
                <MoreVert />
              </IconButton>
            </Tooltip>
            <Button variant="contained" startIcon={<Add />} onClick={openCreate} fullWidth={isMobile}>
              Add plan
            </Button>
          </Stack>
        </Stack>
      )}

      <Menu
        anchorEl={headerMenuAnchor}
        open={!!headerMenuAnchor}
        onClose={() => setHeaderMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => { setHeaderMenuAnchor(null); openCreate(); }}>
          <ListItemIcon><Add fontSize="small" /></ListItemIcon>
          <ListItemText>Add subscription plan</ListItemText>
        </MenuItem>
      </Menu>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {[
          { label: 'Total plans', value: stats.total, color: theme.palette.primary.main },
          { label: 'Active', value: stats.active, color: theme.palette.success.main },
          { label: 'Partners subscribed', value: stats.subscribers, color: theme.palette.warning.main },
        ].map((kpi) => (
          <Grid item xs={12} sm={4} key={kpi.label}>
            <PaperLikeKpi label={kpi.label} value={kpi.value} color={kpi.color} />
          </Grid>
        ))}
      </Grid>

      {mutationError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMutationError('')}>{mutationError}</Alert>
      )}

      {isLoading && <LinearProgress sx={{ mb: 2 }} />}

      {!isLoading && !plans.length && (
        <Alert severity="info" icon={<CardMembership />} sx={PARTNER_PLAN_ALERT_SX}>
          No subscription plans yet{zoneName ? ` for ${zoneName}` : ''}. Add a plan so drivers can subscribe during registration.
        </Alert>
      )}

      {!!plans.length && (isMobile || compact) ? (
        <Grid container spacing={2}>
          {plans.map((plan) => (
            <Grid item xs={12} sm={6} key={plan._id}>
              <PlanCard
                plan={plan}
                onEdit={() => openEdit(plan)}
                onMenu={(el) => openMenu(el, plan)}
                onViewPartners={() => openSubscribers(plan)}
                onToggleStatus={handleStatusToggle}
                statusToggleDisabled={statusMut.isPending && statusToggleId === plan._id}
              />
            </Grid>
          ))}
        </Grid>
      ) : !!plans.length && (
        <Box sx={{ height: 520, bgcolor: 'background.paper', borderRadius: 2, border: 1, borderColor: 'divider' }}>
          <DataGrid
            rows={plans}
            columns={columns}
            loading={isLoading}
            getRowId={(r) => r._id}
            rowHeight={56}
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25]}
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            onRowDoubleClick={(p) => openEdit(p.row as PartnerSubscriptionPlanRow)}
            sx={PARTNER_PLAN_GRID_SX}
          />
        </Box>
      )}

      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={() => menuPlan && openEdit(menuPlan)}>
          <ListItemIcon><Edit fontSize="small" /></ListItemIcon>
          <ListItemText>Edit plan</ListItemText>
        </MenuItem>
        <MenuItem
          disabled={(menuPlan?.activeSubscriberCount ?? 0) > 0}
          onClick={() => menuPlan && prepareAction(menuPlan, 'delete')}
        >
          <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>
            Delete
            {(menuPlan?.activeSubscriberCount ?? 0) > 0 ? ' (in use)' : ''}
          </ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => menuPlan && openSubscribers(menuPlan)}
        >
          <ListItemIcon><People fontSize="small" /></ListItemIcon>
          <ListItemText>
            View subscribed partners
            {(menuPlan?.activeSubscriberCount ?? 0) > 0 ? ` (${menuPlan?.activeSubscriberCount})` : ''}
          </ListItemText>
        </MenuItem>
      </Menu>

      <PartnerSubscriptionPlanFormDrawer
        open={drawerOpen}
        mode={mode}
        plan={selected}
        loading={createMut.isPending || updateMut.isPending}
        error={formError}
        defaultZoneId={defaultZoneId}
        onClose={() => setDrawerOpen(false)}
        onSubmit={(payload) => {
          const body = zoneId
            ? { ...payload, zoneIds: [zoneId] }
            : payload;
          if (mode === 'create') createMut.mutate(body);
          else if (selected) updateMut.mutate({ id: selected._id, payload: body });
        }}
      />

      <PartnerPlanSubscribersDrawer
        open={!!subscribersPlan}
        onClose={() => setSubscribersPlan(null)}
        plan={subscribersPlan}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete subscription plan?"
        message={
          usagePreview && usagePreview.activeSubscriberCount > 0
            ? `Cannot delete — ${usagePreview.activeSubscriberCount} partner(s) use "${deleteTarget?.name}". Deactivate the plan instead.`
            : `Permanently delete "${deleteTarget?.name}"? This cannot be undone.`
        }
        confirmLabel={usagePreview?.canDelete ? 'Delete' : 'OK'}
        confirmColor="error"
        loading={deleteMut.isPending}
        onCancel={() => { setDeleteTarget(null); setUsagePreview(null); }}
        onConfirm={() => {
          if (usagePreview?.canDelete && deleteTarget) deleteMut.mutate(deleteTarget._id);
          else { setDeleteTarget(null); setUsagePreview(null); }
        }}
      />

      <ConfirmDialog
        open={!!deactivateTarget}
        title="Deactivate subscription plan?"
        message={
          usagePreview && usagePreview.activeSubscriberCount > 0
            ? `"${deactivateTarget?.name}" has ${usagePreview.activeSubscriberCount} active partner(s). Deactivating hides it from new registrations; existing partners keep their plan.`
            : `Deactivate "${deactivateTarget?.name}"? It will no longer appear during driver registration.`
        }
        confirmLabel="Deactivate"
        confirmColor="warning"
        loading={statusMut.isPending}
        onCancel={() => { setDeactivateTarget(null); setUsagePreview(null); }}
        onConfirm={() => deactivateTarget && statusMut.mutate({ id: deactivateTarget._id, isActive: false })}
      />
    </Box>
  );
}

function PaperLikeKpi({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <Box sx={{
      p: 2, borderRadius: 2, border: 1, borderColor: alpha(color, 0.25),
      bgcolor: alpha(color, 0.06),
    }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600}>{label}</Typography>
      <Typography variant="h5" fontWeight={800} sx={{ color }}>{value}</Typography>
    </Box>
  );
}
